import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { CircularProgress, Stack } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiUpload } from "react-icons/fi";
import { VscFilePdf } from "react-icons/vsc";
import { Item } from "semantic-ui-react";

import { getBase64 } from "../../../util/base64";
import { Button } from "../../Form";
import { Color } from "../../Form/Button";
import Modal from "../Modal";
import useStyles from "./styles";

interface FileData {
  file: File;
  name: string;
}

export interface UploadCSVErrors {
  [x: string]: string[];
}

export interface UploadCSVFileProps {
  modalTitle?: string;
  onConfirmUpload?: (uri: string, fileUploaded?: File) => void;
  lineErrors?: UploadCSVErrors;
  generalErrors?: UploadCSVErrors;
  onCloseModal?: () => void;
  onDownloadTemplate?: () => void;
  loadingUpload?: boolean;
  textSave?: string;
  className?: string;
  maxSize?: number;
}

export interface UploadCSVFileHandles {
  open: () => void;
  close: () => void;
}

const UploadCSVFile: ForwardRefRenderFunction<UploadCSVFileHandles, UploadCSVFileProps> = (
  props,
  ref
) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const {
    modalTitle = translate("uploadFile"),
    onConfirmUpload,
    lineErrors = {},
    generalErrors = {},
    onCloseModal: onCloseModalExternal,
    onDownloadTemplate,
    loadingUpload,
    textSave = translate("upload"),
    className = classes.uploadModal,
    maxSize,
  } = props;

  const existErrors = Object.keys({ ...generalErrors, ...lineErrors }).length > 0;

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [fileData, setFileData] = useState<FileData[]>([]);
  const existFile = !!fileData?.length;

  const onOpenModal = useCallback(() => {
    setModalOpened(true);
  }, []);
  const onCloseModal = useCallback(() => {
    setModalOpened(false);
    setFileData([]);
    if (onCloseModalExternal) onCloseModalExternal();
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  const onDropFile = (files: File[]) => {
    if (!files?.length) return;

    const file = files[0];

    setFileData([
      {
        file,
        name: file.name,
      },
    ]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onDropFile,
    maxFiles: 1,
    accept: [".csv", "text/csv"],
  });

  const renderDropzone = () => {
    return (
      <div {...getRootProps()} className={classes.dropzone}>
        <input {...getInputProps()} />
        <Stack
          id="dropBtn"
          justifyContent={"center"}
          alignItems={"center"}
          className={classes.button}
        >
          <Item className={classes.textButton}>
            <FiUpload size={24} />
          </Item>
          <Item className={classes.textButton}>{translate("dragOrClickToUploadYourFile")}</Item>
          {maxSize && (
            <Item className={classes.textButtonInfo}>
              {translate("maxSize")}:{" "}
              <span className={classes.textButtonFormat}>
                {maxSize}
                {translate("MB")}
              </span>
            </Item>
          )}
          <Item className={classes.textButtonInfo}>
            {translate("formats")}:{" "}
            <span className={classes.textButtonFormat}>{translate("csv")}</span>
          </Item>
        </Stack>
      </div>
    );
  };

  const showLineErrors = () => {
    if (!existErrors) return null;

    return (
      <Grid container direction="column">
        <Grid item xs={12} className={classes.errorTitle}>
          <span>
            {`${translate("on")} `}
            <b>{`${fileData.map(({ name }) => name).join(", ")}`}</b>,
            {` ${translate("weHaveFoundSomeErrors")}`}.
          </span>
        </Grid>
        <Grid item xs={12} className={classes.errorTitle}>
          <span>{translate("youShouldAdjustThoseLinesToproceed")}:</span>
        </Grid>
        {Object.values(generalErrors).flatMap((error, index) => {
          return (
            <Grid item xs={12} key={index} className={classes.errorLine}>
              <span>{error}</span>
            </Grid>
          );
        })}
        {Object.keys(lineErrors).map((lineWithError) => {
          const errors = lineErrors[lineWithError] || [];

          return errors.map((error, index) => (
            <Grid item xs={12} key={index} className={classes.errorLine}>
              <span>{`Line ${lineWithError} - ${error}`}</span>
            </Grid>
          ));
        })}
      </Grid>
    );
  };

  const onRemoveFileFromData = (fileIndex: number) => {
    const data = [...fileData];

    data.splice(fileIndex, 1);
    setFileData(data);
  };

  const showFile = () => {
    if (existErrors) {
      return showLineErrors();
    }

    return fileData.map((fileContent, index) => (
      <div className={classes.showFile} key={index}>
        <div className={classes.showFileLeft}>
          <VscFilePdf size={16} className={classes.redColor} />
          <span className={classes.filename}>{fileContent.name}</span>
        </div>

        <Button
          ghost={true}
          startIcon={<AiOutlineCloseCircle size={20} className={classes.redColor} />}
          onClick={() => onRemoveFileFromData(index)}
        ></Button>
      </div>
    ));
  };

  const renderLoading = () => {
    return (
      <div className={classes.loadingMainDiv}>
        <div className={classes.loadingContainer}>
          <span className={classes.loadingTitle}>
            {translate("weAreLoadingTheUploadedContentPleaseWait")}
          </span>

          <CircularProgress className={classes.loadingProgress} />
          <div></div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <Grid id="bodyContainer" container spacing={4} direction="row">
        <Grid id="bodyContent" item sm={12}>
          {!existFile ? renderDropzone() : showFile()}
        </Grid>
      </Grid>
    );
  };

  const onSubmitModal = async () => {
    if (!existFile) return;

    const uriCSV = await getBase64<string>(fileData[0].file, "text");

    if (onConfirmUpload) onConfirmUpload(uriCSV, fileData[0].file);
  };

  const downloadTemplateButtonProps = onDownloadTemplate && {
    textSave: translate("downloadTemplate"),
    classesSave: classes.downloadTemplate,
    customColorSave: Color.INFO,
    onSave: () => onDownloadTemplate(),
  };

  return (
    <Modal
      className={className}
      open={modalOpened}
      showActionButtons={!loadingUpload}
      showLeftActionButtons
      actionLeftButtonProps={downloadTemplateButtonProps}
      actionButtonProps={{
        onCancel: onCloseModal,
        onSave: onSubmitModal,
        textSave,
        loading: loadingUpload,
        cancelDisabled: loadingUpload,
        saveDisabled: !existFile || existErrors,
      }}
      title={modalTitle ?? !loadingUpload ? modalTitle : ""}
      handleClose={!loadingUpload ? onCloseModal : undefined}
      showModalFooter={!loadingUpload}
    >
      {!loadingUpload ? renderContent() : renderLoading()}
    </Modal>
  );
};

export default forwardRef(UploadCSVFile);
